import { ChangeEvent, useRef, useState } from 'react';
import { FaUpload } from '../Icon/reactIcons';
import { MaxImageSize, FileTypes } from '../../../constants/MaxImageSize';
import { DefaultLayoutPadding } from '../../../constants/Layout';
import theme from '../../../theme';
import { uploadFile, useBreakpoints } from '../../../utils';
import { FlexContainer } from '../Container';
import { ErrorIcon, SuccessIconAnimated } from '../icons';
import { Description, Label } from '../InputText/style';
import { LoadingDots } from '../Loading';
import { Error, Text } from '../Text';
import { UploadContainer } from './style';
import * as Sentry from '@sentry/nextjs';

type Props = {
  error?: string;
  onUpload: (uploadUrl: string) => void;
  title?: string;
  uploadFolder: string;
  onClick?: () => void;
  isValidate?: boolean;
};
type UploadStatus = 'Pending' | 'Fail' | 'Success' | 'Notvalid';
const Upload = ({ error, title, onClick, onUpload, uploadFolder, isValidate = false }: Props) => {
  const { isSmallScreen } = useBreakpoints();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('Pending');
  const fileRef = useRef<HTMLInputElement>();

  const handleFileChange = async (e: ChangeEvent) => {
    setUploadStatus('Pending');
    setUploadedFile(null);
    const file = (e.target as HTMLInputElement).files[0];
    if (!file) return;
    if (file.size >= MaxImageSize.Common) {
      alert(`File size should be less than ${MaxImageSize.Common / 1000000}MB`);
      return;
    }
    if (
      isValidate &&
      FileTypes?.types &&
      FileTypes.types?.length > 0 &&
      !FileTypes?.types.includes(file.type.split('/').pop().toLocaleLowerCase())
    ) {
      setUploadStatus('Notvalid');
      setUploadedFile(file);
    } else {
      setUploadedFile(file);
      handleUpload(file);
    }
  };
  const handleUpload = async (file: File) => {
    if (file) {
      try {
        const f = await uploadFile(file, uploadFolder);
        if (!f.url) setUploadStatus('Fail');
        else {
          setUploadStatus('Success');
          onUpload(f.url);
        }
      } catch (error) {
        Sentry.captureException(error);
        setUploadStatus('Fail');
      }
    }
  };
  const handleClick = () => {
    if (fileRef.current) fileRef.current.click();
  };
  return (
    <FlexContainer style={{ position: 'relative', maxWidth: 'fit-content' }} flexDirection="column" gap="xs">
      <UploadContainer
        error={error?.length > 0}
        gap={!isSmallScreen ? 'xs' : 'xxs'}
        style={{ cursor: 'pointer' }}
        onClick={onClick ? onClick : handleClick}
        breaks="sm"
        flexDirection="column"
      >
        {title && <Label>{title}</Label>}
        {error && !uploadedFile && (
          <Error style={{ position: 'absolute', top: '22px', paddingLeft: theme.spacing[DefaultLayoutPadding] }}>
            {error}
          </Error>
        )}
        <FlexContainer padLeft={true} gap="xs" align="center" justify="start">
          <FaUpload />
          {!uploadedFile && <Text>Click here to upload a file</Text>}
          {uploadedFile && (
            <FlexContainer gap="xs" align="center">
              <Description style={{ margin: 0 }}>{uploadedFile.name}</Description>
              {uploadStatus === 'Pending' ? (
                <LoadingDots />
              ) : uploadStatus === 'Success' ? (
                <SuccessIconAnimated width="16px" />
              ) : (
                <ErrorIcon />
              )}
            </FlexContainer>
          )}
        </FlexContainer>
        <input
          name="Upload"
          type="file"
          onChange={handleFileChange}
          id={'qualification-upload'}
          accept="*"
          ref={fileRef}
          style={{ display: 'none' }}
        />

        {uploadStatus === 'Notvalid' && (
          <FlexContainer padLeft={true} gap="xs" align="center" justify="start">
            <Text style={{ color: 'red' }}>{`Only document files (${FileTypes?.types
              .join(', ')
              .toUpperCase()}) are supported for this section.`}</Text>
          </FlexContainer>
        )}
        {uploadStatus === 'Fail' && (
          <FlexContainer padLeft={true} gap="xs" align="center" justify="start">
            <Text style={{ color: 'red' }}>
              Upload failed. Please check your connection and try again. If the problem persists, contact support.
            </Text>
          </FlexContainer>
        )}
      </UploadContainer>
    </FlexContainer>
  );
};

export default Upload;
